import { candles } from '~data';
import { transactions } from '~/stores/transactions-store';
import { costOf, ordered } from '~/stores/positions';

/**
 * What one loan's money has been worth, day by day.
 *
 * The same two lines the account chart draws, asked of a loan: cost is what the
 * borrowing put in, value is what that is worth now. The gap between them is
 * whether borrowing was worth doing — which is the question the Loans page
 * exists for and the one no single figure on it answers over time.
 *
 * The loan's share of a position is the share of its cost base the loan paid
 * for, so the units attributable to it are that fraction of the position's
 * units. That fraction is not fixed: a sale relieves the borrowed cost pro rata
 * along with the rest, so the walk has to carry the whole position as well as
 * the loan's part of it. Tracking only the loan-funded buys would leave a
 * liquidated position still drawn at full value.
 */
export interface LoanPoint {
    t: number;
    value: number;
    /** Cost base still attributable to this loan. */
    cost: number;
}

interface Tracked {
    symbol: string;
    units: number;
    bookCost: number;
    /** The part of bookCost this loan paid for. */
    loanCost: number;
}

export const loanSeries = (loanId: string): LoanPoint[] => {
    const trades = ordered(transactions);

    // Positions this loan ever bought into. Every trade in one of them counts,
    // not just the loan-funded ones: a cash top-up changes the average cost the
    // loan's share is measured against, and a sale relieves that share.
    const funded = new Set(trades.filter((t) => t.loanId === loanId && t.kind === 'buy').map((t) => `${t.accountId} ${t.symbol}`));
    if (funded.size === 0) return [];

    const relevant = trades.filter((t) => funded.has(`${t.accountId} ${t.symbol}`));
    const symbols = [...new Set(relevant.map((t) => t.symbol))];

    // One pass per symbol with a cursor, same as account-series: a lookup per
    // symbol per day rescans the whole price series thousands of times.
    const prices = new Map(symbols.map((s) => [s, candles(s)]));
    const cursor = new Map(symbols.map((s) => [s, 0]));
    const lastClose = new Map<string, number>();
    const held = new Map<string, Tracked>();

    const first = trades.find((t) => t.loanId === loanId)!.date;
    const dates = [...new Set(symbols.flatMap((s) => (prices.get(s) ?? []).map((c) => c.date)))].filter((d) => d >= first).sort();

    const out: LoanPoint[] = [];
    let next = 0;

    for (const date of dates) {
        while (next < relevant.length && relevant[next].date <= date) {
            const t = relevant[next];
            const key = `${t.accountId} ${t.symbol}`;
            const p = held.get(key) ?? { symbol: t.symbol, units: 0, bookCost: 0, loanCost: 0 };
            held.set(key, p);

            if (t.kind === 'buy') {
                const cost = costOf(t);
                p.units += t.units;
                p.bookCost += cost;
                if (t.loanId === loanId) p.loanCost += cost;
            } else {
                // Average cost: a sale disposes of the same fraction of every
                // dollar in the position, borrowed or not.
                const sold = Math.min(t.units, p.units);
                const share = p.units > 0 ? sold / p.units : 0;
                p.units -= sold;
                p.bookCost -= p.bookCost * share;
                p.loanCost -= p.loanCost * share;
            }
            next += 1;
        }

        let value = 0;
        let cost = 0;
        for (const p of held.values()) {
            cost += p.loanCost;
            if (p.bookCost <= 0 || p.units <= 0) continue;

            const series = prices.get(p.symbol) ?? [];
            let at = cursor.get(p.symbol) ?? 0;
            while (at < series.length && series[at].date <= date) {
                lastClose.set(p.symbol, series[at].close);
                at += 1;
            }
            cursor.set(p.symbol, at);

            // The last close on or before today, so a fund that did not trade
            // carries rather than putting a notch in the line.
            const close = lastClose.get(p.symbol);
            if (close != null) value += p.units * (p.loanCost / p.bookCost) * close;
        }

        out.push({ t: Date.parse(date), value, cost });
    }

    return out;
};
